import {
    cartStore$,
    cartTransientStore$,
    CART_STALE_TIME,
    type AvailabilityItem,
    type FastCartTotalsData,
} from './cart.store';
import { type CartItemInterface, type Cart, CurrencyEnum, type Discount } from '../types/front.graphql';
import {
    addItemWithQuantity,
    calculateItemTotals,
    calculateTotal,
    removeItem,
    removeItemOrQuantity,
    updateItemWithQuantity,
} from '../helpers/cart-item.helpers';

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
// Set to true to enable debug logging for cart store operations
// Set to false in production to reduce console noise
const DEBUG_CART_STORE = true;

// Helper function for conditional logging
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debugLog = (...args: any[]) => {
    if (DEBUG_CART_STORE) {
        console.log(...args);
    }
};

/**
 * SAFETY: Extract primitive cartId value from Legend-State observable.
 * Legend-State may return an observable metadata wrapper object
 * (e.g., { __legend_primitive: "...", id: "cartId" }) from corrupted
 * IndexedDB persistence instead of a plain string.
 */
const extractCartIdPrimitive = (raw: unknown): string => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw !== null && '__legend_primitive' in raw) {
        return String((raw as Record<string, unknown>).__legend_primitive);
    }
    return String(raw);
};

// Cart action functions to replace reducer pattern
export const cartStoreActions = {
    // ============================================================================
    // VALIDATION HELPERS - CRITICAL FOR DATA INTEGRITY
    // ============================================================================

    /**
     * Validate that all cart items have required fields (product name)
     * Throws error if validation fails
     * @param items - Cart items to validate
     * @param context - Context string for error message (e.g., "addItem", "setItems")
     */
    validateItems: (items: CartItemInterface[], context: string = 'unknown') => {
        const validItems = items.filter((item): item is CartItemInterface => item != null);
        const itemsWithoutName = validItems.filter(item => !item.product?.name);
        if (itemsWithoutName.length > 0) {
            const itemIds = itemsWithoutName.map(item => item.id).join(', ');
            const errorMessage = `[CartStore][${context}] Validation Error: Found ${itemsWithoutName.length} item(s) without product name. Item IDs: ${itemIds}`;
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
    },

    // ============================================================================
    // INTERNAL HELPER FUNCTIONS - PRIVATE METHODS FOR STORE MUTATIONS
    // ============================================================================

    /**
     * PRIVATE: Internal method to set items to store
     * This is the ONLY place where cartStore$.items.set() should be called
     * @param items - Validated cart items
     * @param context - Context for logging
     */
    _setItemsToStore: (items: CartItemInterface[], context: string = 'unknown') => {
        debugLog(`[CartStore][_setItemsToStore][${context}] Setting ${items.length} items to store`);
        // Deep-clone to avoid "object is not extensible" errors when Legend-State
        // persists frozen Apollo Client objects to IndexedDB.
        const clonedItems = structuredClone(items);
        cartStore$.items.set(clonedItems);
        cartStore$.totalItems.set(clonedItems.length);
        debugLog(`[CartStore][_setItemsToStore][${context}] Successfully set items to store`);
    },

    /**
     * PRIVATE: Internal method to set cart ID to store
     * This is the ONLY place where cartStore$.cartId.set() should be called
     * @param id - Cart ID
     * @param context - Context for logging
     */
    _setCartIdToStore: (id: string | undefined, context: string = 'unknown') => {
        debugLog(`[CartStore][_setCartIdToStore][${context}] Setting cartId:`, id);
        cartStore$.cartId.set(id ?? '');
    },

    /**
     * PRIVATE: Internal method to set cart data to store
     * This is the ONLY place where cartStore$.cartData.set() should be called
     * @param data - Cart data
     * @param context - Context for logging
     */
    _setCartDataToStore: (data: Cart | undefined, context: string = 'unknown') => {
        debugLog(`[CartStore][_setCartDataToStore][${context}] Setting cart data`);
        // Deep-clone to avoid "object is not extensible" errors with frozen Apollo objects
        cartStore$.cartData.set(data ? structuredClone(data) : data);
    },

    /**
     * PRIVATE: Internal method to set last fetched timestamp
     * This is the ONLY place where cartStore$.lastFetchedAt.set() should be called
     * @param timestamp - Timestamp
     * @param context - Context for logging
     */
    _setLastFetchedAtToStore: (timestamp: number | undefined, context: string = 'unknown') => {
        debugLog(`[CartStore][_setLastFetchedAtToStore][${context}] Setting timestamp:`, timestamp);
        cartStore$.lastFetchedAt.set(timestamp);
    },

    /**
     * PRIVATE: Internal method to set last synced timestamp
     * This is the ONLY place where cartStore$.lastSyncedAt.set() should be called
     * @param timestamp - Timestamp
     * @param context - Context for logging
     */
    _setLastSyncedAtToStore: (timestamp: number | undefined, context: string = 'unknown') => {
        debugLog(`[CartStore][_setLastSyncedAtToStore][${context}] Setting timestamp:`, timestamp);
        cartStore$.lastSyncedAt.set(timestamp);
    },

    /**
     * PRIVATE: Internal method to set loading states to transient store
     * @param loading - Loading state object
     * @param context - Context for logging
     */
    _setLoadingStatesToStore: (
        loading: { isCartLoading?: boolean; isFetchingCart?: boolean; isCartSetupLoading?: boolean },
        context: string = 'unknown'
    ) => {
        debugLog(`[CartStore][_setLoadingStatesToStore][${context}] Setting loading states:`, loading);
        if (loading.isCartLoading !== undefined) {
            cartTransientStore$.isCartLoading.set(loading.isCartLoading);
        }
        if (loading.isFetchingCart !== undefined) {
            cartTransientStore$.isFetchingCart.set(loading.isFetchingCart);
        }
        if (loading.isCartSetupLoading !== undefined) {
            cartTransientStore$.isCartSetupLoading.set(loading.isCartSetupLoading);
        }
    },

    // ============================================================================
    // LOADING STATE MANAGEMENT - Access to TRANSIENT store
    // ============================================================================

    setCartActionsLoading: (isLoading: boolean) => {
        cartStoreActions._setLoadingStatesToStore({ isCartLoading: isLoading }, 'setCartActionsLoading');
    },

    setFetchingCartLoading: (isLoading: boolean) => {
        cartStoreActions._setLoadingStatesToStore({ isFetchingCart: isLoading }, 'setFetchingCartLoading');
    },

    setCartSetupLoading: (isLoading: boolean) => {
        cartStoreActions._setLoadingStatesToStore({ isCartSetupLoading: isLoading }, 'setCartSetupLoading');
    },

    // ============================================================================
    // TRANSIENT STATE INITIALIZATION - App Start/Resume
    // ============================================================================

    /**
     * Reset all transient (non-persistent) loading states
     * Call this when app starts or returns from background
     */
    resetTransientStates: () => {
        debugLog('[CartStore] Resetting transient states (loading flags)');
        cartStoreActions._setLoadingStatesToStore(
            {
                isCartLoading: false,
                isFetchingCart: false,
                isCartSetupLoading: false,
            },
            'resetTransientStates'
        );
        // Also reset fastCartTotalsLoading — not part of _setLoadingStatesToStore
        // but still a transient loading flag that can get stuck after page refresh
        // or unexpected interruption during checkout totals fetch.
        cartTransientStore$.fastCartTotalsLoading.set(false);
    },

    /**
     * Initialize cart store on app start
     * Resets transient states that should not persist
     */
    initializeCartStore: () => {
        debugLog('[CartStore] Initializing cart store on app start');
        cartStoreActions.resetTransientStates();
    },

    // ============================================================================
    // TIMESTAMP MANAGEMENT - For Staleness Tracking (PERSISTENT store)
    // ============================================================================

    /**
     * Update last fetched timestamp
     * Call this after successful cart fetch
     */
    updateLastFetchedAt: () => {
        const timestamp = Date.now();
        debugLog('[CartStore] Updating lastFetchedAt:', new Date(timestamp).toISOString());
        cartStoreActions._setLastFetchedAtToStore(timestamp, 'updateLastFetchedAt');
    },

    /**
     * Update last synced timestamp
     * Call this after successful cart sync
     */
    updateLastSyncedAt: () => {
        const timestamp = Date.now();
        debugLog('[CartStore] Updating lastSyncedAt:', new Date(timestamp).toISOString());
        cartStoreActions._setLastSyncedAtToStore(timestamp, 'updateLastSyncedAt');
    },

    /**
     * Get last fetched timestamp
     * Uses peek() to avoid creating reactive subscriptions
     */
    getLastFetchedAt: (): number | undefined => {
        return cartStore$.lastFetchedAt.peek();
    },

    /**
     * Get last synced timestamp
     * Uses peek() to avoid creating reactive subscriptions
     */
    getLastSyncedAt: (): number | undefined => {
        return cartStore$.lastSyncedAt.peek();
    },

    /**
     * Check if cart data is stale (older than CART_STALE_TIME)
     * Uses peek() to avoid creating reactive subscriptions
     */
    isCartDataStale: (): boolean => {
        const lastFetched = cartStore$.lastFetchedAt.peek();

        // No timestamp = definitely stale
        if (!lastFetched) {
            debugLog('[CartStore] No lastFetchedAt timestamp - data is stale');
            return true;
        }

        const now = Date.now();
        const age = now - lastFetched;
        const isStale = age > CART_STALE_TIME;

        debugLog('[CartStore] Data age:', Math.round(age / 1000), 'seconds, stale:', isStale);
        return isStale;
    },

    /**
     * Check if should refresh cart
     * Takes into account staleness and authentication status
     * Uses peek() to avoid creating reactive subscriptions
     */
    shouldRefreshCart: (isAuthenticated: boolean): boolean => {
        const isStale = cartStoreActions.isCartDataStale();
        const hasCartId = !!cartStoreActions.getCartId();

        // Always refresh if:
        // 1. No cart data yet
        // 2. Data is stale
        // 3. User is authenticated (to sync with server)
        if (!hasCartId) {
            debugLog('[CartStore] No cartId - should not refresh');
            return false;
        }

        if (isStale) {
            debugLog('[CartStore] Data is stale - should refresh');
            return true;
        }

        if (isAuthenticated) {
            debugLog('[CartStore] User authenticated - should refresh to sync');
            return true;
        }

        debugLog('[CartStore] Data is fresh and user not authenticated - no need to refresh');
        return false;
    },

    // ============================================================================
    // STATE GETTERS - CONTROLLED ACCESS TO INTERNAL STATE
    // ============================================================================

    // Loading state getters - Access TRANSIENT store
    // Uses peek() to avoid creating reactive subscriptions
    getLoadingState: () => ({
        cartActions: cartTransientStore$.isCartLoading.peek(),
        fetchingCart: cartTransientStore$.isFetchingCart.peek(),
        cartSetup: cartTransientStore$.isCartSetupLoading.peek(),
        isAnyLoading:
            cartTransientStore$.isCartLoading.peek() ||
            cartTransientStore$.isFetchingCart.peek() ||
            cartTransientStore$.isCartSetupLoading.peek(),
    }),

    // Individual loading state getters
    // Uses peek() to avoid creating reactive subscriptions
    isCartActionsLoading: () => cartTransientStore$.isCartLoading.peek(),
    isFetchingCart: () => cartTransientStore$.isFetchingCart.peek(),
    isFetchingCartObservable: () => cartTransientStore$.isFetchingCart,
    isCartSetupLoading: () => cartTransientStore$.isCartSetupLoading.peek(),
    isAnyLoading: () => {
        return (
            cartTransientStore$.isCartLoading.peek() ||
            cartTransientStore$.isFetchingCart.peek() ||
            cartTransientStore$.isCartSetupLoading.peek()
        );
    },

    // Cart data getters - Access PERSISTENT store
    // Uses peek() to avoid creating reactive subscriptions
    // SAFETY: Extract primitive value in case Legend-State returns observable
    // metadata wrapper (e.g., { __legend_primitive: "...", id: "cartId" })
    // from corrupted IndexedDB persistence.
    getCartId: (): string => {
        const raw: unknown = cartStore$.cartId.peek();
        return extractCartIdPrimitive(raw);
    },

    /**
     * Get cartId as observable for reactive subscriptions
     * Use this when you need to react to cartId changes
     * Example: const cartId = useSelector(cartStoreActions.getCartIdObservable)
     */
    getCartIdObservable: () => cartStore$.cartId,

    getCartItems: () => (cartStore$.items.peek() ?? []).filter((item): item is CartItemInterface => item != null),
    getCartTotal: () =>
        cartStore$.cartData?.prices?.grand_total?.peek() || {
            value: 0,
            currency: CurrencyEnum.Vnd,
            __typename: 'Money',
        },

    // ============================================================================
    // STATE GETTERS - INTERNAL USE ONLY
    // ============================================================================

    getCartData: (): Cart | undefined => {
        return cartStore$.cartData.peek();
    },

    getCartPrices: () => {
        const cartData = cartStore$.cartData.peek();
        return cartData?.prices;
    },

    getCurrency: (): CurrencyEnum => {
        const cartData = cartStore$.cartData.peek();
        return (cartData?.prices?.grand_total?.currency as CurrencyEnum) ?? CurrencyEnum.Vnd;
    },

    getDiscounts: (): Discount[] | undefined => {
        const cartData = cartStore$.cartData.peek();
        return (
            cartData?.prices?.discounts?.filter((d: Discount | null | undefined): d is Discount => d !== null) ??
            undefined
        );
    },

    // ============================================================================
    // OBSERVABLE GETTERS - FOR REACTIVE SUBSCRIPTIONS
    // ============================================================================

    /**
     * Get cart items as observable for reactive subscriptions
     * Use with useSelector: const items = useSelector(() => cartStoreActions.getCartItemsObservable().get())
     */
    getCartItemsObservable: () => cartStore$.items,

    /**
     * Get cart data as observable for reactive subscriptions
     * Use with useSelector: const cartData = useSelector(() => cartStoreActions.getCartDataObservable().get())
     */
    getCartDataObservable: () => cartStore$.cartData,

    /**
     * Get cart prices as observable for reactive subscriptions
     * Use with useSelector: const prices = useSelector(() => cartStoreActions.getCartPricesObservable().get())
     */
    getCartPricesObservable: () => cartStore$.cartData.prices,

    /**
     * Get currency as observable for reactive subscriptions
     * Use with useSelector: const currency = useSelector(() => cartStoreActions.getCurrencyObservable().get())
     */
    getCurrencyObservable: () => cartStore$.cartData.prices.grand_total.currency,

    /**
     * Get discounts as observable for reactive subscriptions
     * Use with useSelector: const discounts = useSelector(() => cartStoreActions.getDiscountsObservable().get())
     */
    getDiscountsObservable: () => cartStore$.cartData.prices.discounts,

    getCartState: () => {
        return {
            cartId: extractCartIdPrimitive(cartStore$.cartId.peek()),
            items: cartStoreActions.getCartItems(),
            totalItems: cartStore$.totalItems.peek() ?? 0,
            cartData: cartStore$.cartData.peek(),
            cartTotal: cartStoreActions.getCartTotal(),
            // Loading states from TRANSIENT store
            isCartLoading: cartTransientStore$.isCartLoading.peek(),
            isFetchingCart: cartTransientStore$.isFetchingCart.peek(),
            // Convenience accessors for common server data
            id: cartStore$.cartData?.id?.peek() || '',
            isVirtual: cartStore$.cartData?.is_virtual?.peek() || false,
            totalQuantity: cartStore$.cartData?.total_quantity?.peek() || 0,
            shippingAddresses: cartStore$.cartData?.shipping_addresses?.peek() || [],
            total: cartStore$.cartData?.prices?.grand_total?.peek() || {
                value: 0,
                currency: CurrencyEnum.Vnd,
                __typename: 'Money',
            },
            // Timestamp tracking from PERSISTENT store
            lastFetchedAt: cartStore$.lastFetchedAt.peek(),
            lastSyncedAt: cartStore$.lastSyncedAt.peek(),
        };
    },

    // ============================================================================
    // CART MUTATIONS - BUSINESS LOGIC ENCAPSULATED (PERSISTENT store)
    // ============================================================================

    setCartId: (id: string) => {
        cartStoreActions._setCartIdToStore(id, 'setCartId');

        // Initialize cartData if it doesn't exist
        // Uses peek() to avoid creating reactive subscriptions
        if (!cartStore$.cartData.peek()) {
            cartStoreActions._setCartDataToStore(
                {
                    id,
                    is_virtual: false,
                    shipping_addresses: [],
                    total_quantity: 0,
                    prices: {
                        grand_total: { value: 0, currency: CurrencyEnum.Vnd, __typename: 'Money' },
                        __typename: 'CartPrices',
                    },
                    items: [],
                    __typename: 'Cart',
                },
                'setCartId:initialize'
            );
        } else {
            cartStore$.cartData.id.set(id);
        }
    },

    addItemsWithQuantity: (items: CartItemInterface[]) => {
        // Validate new items before adding
        cartStoreActions.validateItems(items, 'addItemsWithQuantity');

        // Uses peek() to avoid creating reactive subscriptions
        const currentItems = cartStore$.items.peek();
        const newItems = [...currentItems, ...items];
        cartStoreActions.updateCartCalculations(newItems);
    },

    addItemWithQuantity: (item: CartItemInterface, quantity: number) => {
        // Validate item before adding
        cartStoreActions.validateItems([item], 'addItemWithQuantity');

        // Uses peek() to avoid creating reactive subscriptions
        const currentItems = cartStore$.items.peek();
        const newItems = addItemWithQuantity(currentItems, item, quantity);
        cartStoreActions.updateCartCalculations(newItems);
    },

    removeItemOrQuantity: (id: CartItemInterface['id'], quantity?: number) => {
        // Uses peek() to avoid creating reactive subscriptions
        const currentItems = cartStore$.items.peek();
        const newItems = removeItemOrQuantity(currentItems, id, quantity ?? 1);
        cartStoreActions.updateCartCalculations(newItems);
    },

    removeItem: (id: CartItemInterface['id']) => {
        // Uses peek() to avoid creating reactive subscriptions
        const currentItems = cartStore$.items.peek();
        const newItems = removeItem(currentItems, id);
        cartStoreActions.updateCartCalculations(newItems);
    },

    updateItemWithQuantity: (id: CartItemInterface['id'], quantity: number) => {
        // Uses peek() to avoid creating reactive subscriptions
        const currentItems = cartStore$.items.peek();
        const newItems = updateItemWithQuantity(currentItems, id, quantity);
        cartStoreActions.updateCartCalculations(newItems);
    },

    setItems: (items: CartItemInterface[], id: string) => {
        // Validate items using centralized validation
        cartStoreActions.validateItems(items, 'setItems');

        // Use internal methods instead of direct calls
        cartStoreActions._setItemsToStore(items, 'setItems');
        cartStoreActions._setCartIdToStore(id, 'setItems');

        // Initialize cartData if needed before setting id
        // Uses peek() to avoid creating reactive subscriptions
        if (!cartStore$.cartData.peek()) {
            cartStoreActions._setCartDataToStore(
                {
                    id,
                    is_virtual: false,
                    shipping_addresses: [],
                    total_quantity: 0,
                    prices: {
                        grand_total: { value: 0, currency: CurrencyEnum.Vnd, __typename: 'Money' },
                        __typename: 'CartPrices',
                    },
                    items: [],
                    __typename: 'Cart',
                },
                'setItems:initialize'
            );
        } else {
            cartStore$.cartData.id.set(id);
        }
        cartStoreActions.updateCartCalculations(items);

        // Update timestamp after successful fetch
        cartStoreActions.updateLastFetchedAt();
        cartStoreActions.updateLastSyncedAt();
    },

    resetCart: () => {
        debugLog('[CartStore] Resetting cart completely');

        // Reset PERSISTENT store by setting each property individually.
        // Legend-State's syncObservable detects each .set() call and
        // automatically syncs the updated values to IndexedDB.
        // DO NOT use cartStore$.set({...}) — Legend-State may not detect
        // individual key changes when replacing the entire object at once,
        // leaving stale values (e.g. cartId) in IndexedDB.
        cartStore$.cartId.delete();
        cartStore$.cartData.delete();
        cartStore$.items.delete();
        cartStore$.totalItems.delete();
        cartStore$.lastFetchedAt.delete();
        cartStore$.lastSyncedAt.delete();
        // cartStore$.availableItems.delete();
        cartStore$.fastCartTotals.delete();

        // Reset TRANSIENT store
        cartStoreActions._setLoadingStatesToStore(
            {
                isCartLoading: false,
                isFetchingCart: false,
                isCartSetupLoading: false,
            },
            'resetCart'
        );
    },

    /**
     * Full cart reset including provider lifecycle flags.
     * Use ONLY for store code changes where the auth effect must re-run
     * to fetch the cart for the new store.
     *
     * DO NOT use for logout — resetCart() is sufficient there because
     * the auth effect already handles the authorized→!authorized transition.
     * Resetting lifecycle flags during logout causes an infinite loop:
     * resetCart → customerCartLoaded=false → auth effect re-runs → fetch → reconcile → resetCart → loop
     */
    resetCartForStoreChange: () => {
        cartStoreActions.resetCart();
        cartTransientStore$.customerCartLoaded.set(false);
        cartTransientStore$.cartInitialized.set(false);
    },

    updateCartLanguage: (language: string) => {
        // This can be extended if language-specific cart updates are needed
        debugLog('Cart language updated to:', language);
    },

    // ============================================================================
    // INTERNAL HELPER FUNCTIONS - NOT EXPOSED
    // ============================================================================

    // Helper function to update cart calculations
    // Uses peek() to avoid creating reactive subscriptions
    updateCartCalculations: (items: CartItemInterface[]) => {
        const calculatedItems = calculateItemTotals(items) as CartItemInterface[];
        // CRITICAL: Validate items before any calculation or storage
        // This ensures no invalid items can enter the store through any mutation
        cartStoreActions.validateItems(calculatedItems, 'updateCartCalculations');

        const total = calculateTotal(items);
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        // Use internal method to set items - NEVER call cartStore$.items.set() directly
        cartStoreActions._setItemsToStore(calculatedItems, 'updateCartCalculations');

        // Initialize cartData if it doesn't exist
        // Uses peek() to avoid creating reactive subscriptions
        if (!cartStore$.cartData.peek()) {
            cartStoreActions._setCartDataToStore(
                {
                    id: extractCartIdPrimitive(cartStore$.cartId.peek()),
                    is_virtual: false,
                    shipping_addresses: [],
                    total_quantity: totalQuantity,
                    prices: {
                        grand_total: { value: total, currency: CurrencyEnum.Vnd, __typename: 'Money' },
                        __typename: 'CartPrices',
                    },
                    items: calculatedItems,
                    __typename: 'Cart',
                },
                'updateCartCalculations:initialize'
            );
        } else {
            // Update server cart data (snake_case GraphQL schema)
            cartStore$.cartData.items.set(calculatedItems);
            cartStore$.cartData.prices?.grand_total?.set({
                ...cartStore$.cartData.prices.grand_total.peek(),
                value: total,
            });
            cartStore$.cartData.total_quantity.set(totalQuantity);
        }
    },

    clearItems: () => {
        cartStoreActions._setItemsToStore([], 'clearItems');
        // Also clear items in cartData if it exists
        // Uses peek() to avoid creating reactive subscriptions
        if (cartStore$.cartData.peek()) {
            cartStore$.cartData.items.set([]);
            cartStore$.cartData.prices?.grand_total?.set({
                ...cartStore$.cartData.prices.grand_total.peek(),
                value: 0,
            });
            cartStore$.cartData.total_quantity.set(0);
        }
    },

    /**
     * ============================================================================
     * CENTRALIZED SERVER SYNC METHOD - Update cart from server response
     * ============================================================================
     *
     * Update cart store with complete data from server response.
     * This is the SINGLE METHOD to use after any cart synchronization operation.
     *
     * Benefits:
     * - Centralized logic for updating cart from server
     * - Consistent handling of all cart properties
     * - Easy to maintain and extend
     * - Prevents scattered update logic across codebase
     *
     * Usage:
     * - After GraphQL mutations (updateCartItems, addSimpleProductsToCart, etc.)
     * - After cart synchronization operations
     * - After fetching cart from server
     *
     * @param serverCart - Complete cart data from server (GraphQL response)
     * @param options - Optional configuration
     */
    updateCartFromServerResponse: (
        serverCart: Cart | undefined | null,
        options?: {
            updateTimestamps?: boolean; // Whether to update lastFetchedAt and lastSyncedAt (default: true)
            clearIfEmpty?: boolean; // Whether to clear cart if no items (default: true)
        }
    ) => {
        const { updateTimestamps = true, clearIfEmpty = true } = options ?? {};

        debugLog('[CartStore] Updating cart from server response', {
            hasCart: !!serverCart,
            cartId: serverCart?.id,
            itemsCount: serverCart?.items?.length ?? 0,
            totalQuantity: serverCart?.total_quantity ?? 0,
            updateTimestamps,
            clearIfEmpty,
        });

        // CASE 1: No cart data from server - clear local cart
        if (!serverCart) {
            console.warn('[CartStore] No cart data from server, clearing local cart');
            if (clearIfEmpty) {
                cartStoreActions.clearItems();
            }
            return;
        }

        // CASE 2: Cart has no items - handle unselect all / empty cart case
        if (!serverCart.items || serverCart.items.length === 0) {
            debugLog('[CartStore] Server cart has no items', {
                clearIfEmpty,
                reason: 'User unselected all or removed all items',
            });

            if (clearIfEmpty) {
                cartStoreActions.clearItems();
                debugLog('[CartStore] Local cart cleared - no items remain');
            }

            // Still update cartId and cartData structure
            if (serverCart.id) {
                cartStoreActions._setCartIdToStore(serverCart.id, 'updateCartFromServerResponse:empty');
            }
            cartStoreActions._setCartDataToStore(serverCart, 'updateCartFromServerResponse:empty');

            if (updateTimestamps) {
                cartStoreActions.updateLastFetchedAt();
                cartStoreActions.updateLastSyncedAt();
            }

            return;
        }

        // CASE 3: Cart has items - update everything
        debugLog('[CartStore] Updating cart with server items', {
            itemsCount: serverCart.items.length,
            totalQuantity: serverCart.total_quantity,
            grandTotal: serverCart.prices?.grand_total?.value,
        });

        try {
            // Validate items from server before updating
            cartStoreActions.validateItems(serverCart.items as CartItemInterface[], 'updateCartFromServerResponse');

            // Update cart items (this will trigger item calculations)
            cartStoreActions._setItemsToStore(serverCart.items as CartItemInterface[], 'updateCartFromServerResponse');

            // Update cart ID
            if (serverCart.id) {
                cartStoreActions._setCartIdToStore(serverCart.id, 'updateCartFromServerResponse');
            }

            // Update complete cart data (includes prices, totals, shipping, etc.)
            cartStoreActions._setCartDataToStore(serverCart, 'updateCartFromServerResponse');

            // Update timestamps if requested
            if (updateTimestamps) {
                cartStoreActions.updateLastFetchedAt();
                cartStoreActions.updateLastSyncedAt();
            }

            debugLog('[CartStore] Successfully updated cart from server response', {
                cartId: serverCart.id,
                itemsCount: serverCart.items.length,
                totalQuantity: serverCart.total_quantity,
                grandTotal: serverCart.prices?.grand_total?.value,
                subtotal: serverCart.prices?.subtotal_excluding_tax?.value,
                discountsCount: serverCart.prices?.discounts?.length ?? 0,
                timestampsUpdated: updateTimestamps,
            });
        } catch (error) {
            console.error('[CartStore] Failed to update cart from server response', error);
            throw error;
        }
    },

    setCartAvailableItems: (itemsMap: Record<string, AvailabilityItem>) => {
        // Deep-clone to avoid "object is not extensible" errors with frozen Apollo objects
        cartStore$.availableItems.set(structuredClone(itemsMap));
    },

    getCartAvailableItemsObservable: () => {
        return cartStore$.availableItems;
    },

    // ============================================================================
    // FAST CART TOTALS - Centralized totals state management
    // ============================================================================

    setFastCartTotals: (totals: FastCartTotalsData | undefined) => {
        // Deep-clone to avoid "object is not extensible" errors with frozen Apollo objects
        cartStore$.fastCartTotals.set(totals ? structuredClone(totals) : undefined);
    },

    clearFastCartTotals: () => {
        cartStore$.fastCartTotals.set(undefined);
    },

    setFastCartTotalsLoading: (loading: boolean) => {
        cartTransientStore$.fastCartTotalsLoading.set(loading);
    },

    getFastCartTotalsObservable: () => cartStore$.fastCartTotals,

    getFastCartTotalsLoadingObservable: () => cartTransientStore$.fastCartTotalsLoading,
};
